import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { metaLead } from "@/lib/pixel";

const HERO = "/nl-schnupperkurs.jpg";

const COURSE_DATE = new Date("2026-08-23T00:00:00+02:00");

const Countdown = () => {
  const [left, setLeft] = useState(COURSE_DATE.getTime() - Date.now());
  useEffect(() => {
    const iv = setInterval(() => setLeft(COURSE_DATE.getTime() - Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const units = [[d, "Tage"], [h, "Std"], [m, "Min"], [s, "Sek"]];
  return (
    <div className="mt-7 w-full max-w-md mx-auto" data-testid="funnel-countdown">
      <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 mb-2.5">Nächster Kurs am <span className="text-[#ff0033]">23.08.</span> – Anmeldung bis 22.08., 23:00 Uhr</p>
      <div className="grid grid-cols-4 gap-2">
        {units.map(([v, l]) => (
          <div key={l} className="border border-[#ff0033]/30 bg-[#0A0A0A] py-3 rounded-lg" style={{ boxShadow: "0 0 18px rgba(255,0,51,0.10)" }}>
            <p className="font-heading text-2xl sm:text-3xl text-white leading-none tabular-nums">{String(v).padStart(2, "0")}</p>
            <p className="font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033] mt-1.5">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SchnupperkursFunnel = () => {
  const nav = useNavigate();

  const TARGET = "/kurse?open=" + encodeURIComponent("schnupperkurs");

  const goToBooking = () => {
    metaLead({ contentName: "Schnupperkurs Funnel", source: "schnupperkurs-ads" });
    nav(TARGET);
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="schnupperkurs-funnel">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 45% at 50% 0%, rgba(255,0,51,0.18) 0%, rgba(0,0,0,0) 60%)" }} />
      <div className="absolute top-[38rem] -left-40 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,0,51,0.10) 0%, rgba(0,0,0,0) 70%)" }} />
      <div className="absolute top-[70rem] -right-40 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,0,51,0.08) 0%, rgba(0,0,0,0) 70%)" }} />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-12 lg:py-16 flex flex-col items-center text-center">
        {/* Brand */}
        <img src="/logo.png" alt="Golfcoaching Academy Wendlingen" className="h-14 w-auto mx-auto mb-8"
             onError={(e) => { e.currentTarget.outerHTML = '<div class=\"font-heading text-xl uppercase tracking-tight mb-8\">Golfcoaching Academy</div>'; }} />

        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-[#ff0033]/50 text-[#ff0033] px-3 py-1.5 font-mono-lab text-[10px] uppercase tracking-widest mb-6 fade-up" style={{ boxShadow: "0 0 24px rgba(255,0,51,0.25)" }}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff0033] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff0033]" />
          </span>
          Golf-Schnupperkurs · Level 1
        </div>

        {/* Headline */}
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight" data-testid="funnel-headline">
          Vom Neugierigen<br /><span className="text-[#ff0033]">zum Golfer</span>
        </h1>

        {/* Sub headline (kurz) */}
        <p className="text-neutral-300 text-base md:text-lg mt-5 max-w-lg mx-auto" data-testid="funnel-subheadline">
          Dein entspannter Einstieg ins Golf – mit Profi-Anleitung und komplettem Leih-Equipment. Ohne Vorkenntnisse.
        </p>

        {/* Hero */}
        <div className="mt-8 w-full max-w-lg mx-auto rounded-lg overflow-hidden border border-white/10 relative group" style={{ boxShadow: "0 0 60px rgba(255,0,51,0.15)" }}>
          <img src={HERO} alt="Schnupperkurs auf der Driving Range der Golfcoaching Academy" className="w-full h-60 sm:h-72 object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-testid="funnel-hero" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
        </div>

        {/* Countdown zum 23.08. */}
        <Countdown />

        {/* CTA – direkt zur Buchungs-Box */}
        <div className="mt-6 w-full max-w-md mx-auto border border-[#ff0033]/25 bg-[#0A0A0A] p-6 sm:p-8 rounded-lg shadow-2xl">
          <button onClick={goToBooking} data-testid="funnel-book-cta"
                  className="w-full bg-[#ff0033] text-white font-heading text-lg uppercase tracking-wider py-4 hover:bg-[#cc0029] transition-colors flex items-center justify-center gap-2">
            Jetzt buchen <ArrowRight size={18} />
          </button>
          <p className="text-neutral-500 text-[11px] leading-relaxed mt-4" data-testid="funnel-cta-note">
            Ohne Vorkenntnisse · Schläger & Bälle inklusive · bis 48 h vorher kostenlos stornierbar.
          </p>
        </div>

        <p className="text-neutral-600 text-[11px] mt-14">© {new Date().getFullYear()} Golfcoaching Academy Wendlingen · GC Kirchheim-Wendlingen</p>
      </div>
    </div>
  );
};

export default SchnupperkursFunnel;
